/**
 * Permission helper utilities for checking route access with the new page_permissions object structure
 */

/**
 * Checks if a user has permission to access a specific route
 * Works with both old (routes array) and new (page_permissions object) permission structures
 * 
 * @param {Object} user - The user object containing permissions
 * @param {string} routePath - The route path to check permissions for
 * @returns {boolean} - Whether the user has permission to access the route
 */
export const hasRoutePermission = (user, routePath) => {
  // Super admin has access to everything
  if (!user) return false;
  if (user.role === "super_admin") return true;
  
  // Clean the route path for consistent comparison
  const clean = (str) =>
    (str || "")
      .trim()
      .replace(/\/\/+/g, "/")
      .replace(/\/+$/, "")
      .replace(/^\/?/, "/")
      .toLowerCase();
  
  const cleanedRoutePath = clean(routePath);
  
  // Check in page_permissions object (new structure)
  if (user.page_permissions && typeof user.page_permissions === 'object') {
    const hasPermissionFromPagePermissions = Object.keys(user.page_permissions).some(perm => {
      const permBase = clean(perm.replace(/\*$/, ""));
      return (
        cleanedRoutePath === permBase ||
        cleanedRoutePath.startsWith(permBase + "/") ||
        permBase.startsWith(cleanedRoutePath + "/") // allows parent route if child exists
      );
    });
    
    if (hasPermissionFromPagePermissions) return true;
  }
  
  // Fallback to routes array (old structure)
  if (user.routes && Array.isArray(user.routes)) {
    return user.routes.some(perm => {
      const permBase = clean(perm.replace(/\*$/, ""));
      return (
        cleanedRoutePath === permBase ||
        cleanedRoutePath.startsWith(permBase + "/") ||
        permBase.startsWith(cleanedRoutePath + "/") // allows parent route if child exists
      );
    });
  }
  
  return false;
};
